import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { PX_PER_PT, fitFontSize, textOverflowMode } from '../utils/badgeText';
import { badgeDocumentHtml, badgeFieldValue, badgeFieldsHtml } from '../utils/badgePrint';
import { isMobilePrint, printBadgeInPage } from '../utils/printWindow';
import { drawBadge } from '../utils/badgeCanvas';
import { badgeCategory, resolveBadgeLayout } from '../utils/badgeLayout';

// DPI for conversion (1 inch = 96 pixels at 96 DPI)
const DPI = 96;

/**
 * Dynamic Badge Renderer Component
 * Renders badges based on configuration and data for both preview and printing
 */
const DynamicBadgeRenderer = forwardRef(({
  badgeConfig,
  userData = null,
  showPreview = false,
  className = '',
  style = {}
}, ref) => {
  const canvasRef = useRef(null);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    getCanvas: () => canvasRef.current,
    printBadge: () => handlePrint(),
    exportToPDF: () => handleExportToPDF(),
    buildPdfFile: () => buildBadgePdf().then(({ pdf, filename }) =>
      new File([pdf.output('blob')], filename, { type: 'application/pdf' })),
  }));

  // Convert inches to pixels
  const inchesToPixels = (inches) => {
    return inches * DPI;
  };

  // Configured size, shrunk only when the text cannot fit its field box.
  const getDynamicFontSize = (text, field) => fitFontSize(text, field);

  // Calculate canvas dimensions based on paper size
  const getCanvasDimensions = () => {
    if (!badgeConfig?.paperSize) {
      return { width: 400, height: 600 };
    }

    const { width, height } = badgeConfig.paperSize;
    const maxDisplayWidth = showPreview ? 300 : 600;
    
    let canvasWidth = inchesToPixels(width);
    let canvasHeight = inchesToPixels(height);
    
    // Scale down for preview if needed
    if (showPreview && canvasWidth > maxDisplayWidth) {
      const scale = maxDisplayWidth / canvasWidth;
      canvasWidth *= scale;
      canvasHeight *= scale;
    }
    
    return { width: canvasWidth, height: canvasHeight };
  };

  // Get field value from userData or use default/dummy data
  const getFieldValue = (field) => badgeFieldValue(field, userData);
  // Fields that print for this attendee (empty / category-hidden ones dropped, gaps closed).
  const getLayout = () => resolveBadgeLayout(badgeConfig?.fields, {
    valueOf: getFieldValue,
    category: badgeCategory(userData),
  });

  // Handle print functionality
  const handlePrint = async () => {
    if (typeof window !== 'undefined' && badgeConfig?.paperSize) {
      try {
        const mobile = isMobilePrint();
        // Opened synchronously in the click so popup blockers allow it.
        const printWindow = mobile ? null : window.open('', '_blank', 'width=800,height=600');
        
        const printFields = badgeFieldsHtml(badgeConfig, userData);
        const printHTML = badgeDocumentHtml(badgeConfig, [printFields], { autoPrint: !mobile });
        if (mobile) {
          return printBadgeInPage({
            fieldsHtml: printFields,
            widthIn: badgeConfig.paperSize.width,
            heightIn: badgeConfig.paperSize.height,
          });
        }

        printWindow.document.write(printHTML);
        printWindow.document.close();
        printWindow.focus();
        return printWindow;

      } catch (error) {
        console.error('Error printing badge:', error);
        alert('Failed to print badge. Please try again.');
      }
    }
  };

  // Handle PDF export functionality
  const handleExportToPDF = async () => {
    if (typeof window !== 'undefined') {
      try {
        const { pdf, filename } = await buildBadgePdf();
        pdf.save(filename);
      } catch (error) {
        console.error('Error exporting to PDF:', error);
        alert('Failed to export PDF. Please try again.');
      }
    }
  };

  /** Renders the badge black-on-white at its exact paper size into a one-page PDF. */
  const buildBadgePdf = async () => {
    const jsPDF = (await import('jspdf')).jsPDF;
    const { width, height } = badgeConfig.paperSize;
    const layout = getLayout();
    const fields = (badgeConfig.fields || [])
      .filter((field) => layout.has(field))
      .map((field) => ({ ...field, ...layout.get(field) }))
      .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');

    const canvas = await drawBadge({
      widthIn: width,
      heightIn: height,
      fields,
      fontSizeFor: (field) => getDynamicFontSize(field.value, field),
    });

    const pdf = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'in',
      format: [width, height],
    });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, width, height, '', 'FAST');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    return { pdf, filename: `badge-${timestamp}.pdf` };
  };

  const canvasDims = getCanvasDimensions();
  const paperSize = badgeConfig?.paperSize;

  if (!badgeConfig || !paperSize) {
    return (
      <div className="flex items-center justify-center p-8 border-2 border-gray-300 border-dashed bg-gray-50">
        <p className="text-gray-500">No badge configuration available</p>
      </div>
    );
  }

  const previewLayout = getLayout();
  const scaleFactorX = canvasDims.width / inchesToPixels(paperSize.width);
  const scaleFactorY = canvasDims.height / inchesToPixels(paperSize.height);

  return (
    <div 
      className={`bg-white border shadow-lg ${className}`}
      style={{ 
        width: `${canvasDims.width}px`, 
        height: `${canvasDims.height}px`,
        ...style 
      }}
    >
      <div
        ref={canvasRef}
        className="relative w-full h-full bg-white"
        style={{
          width: `${canvasDims.width}px`,
          height: `${canvasDims.height}px`,
          backgroundImage: showPreview && badgeConfig.backgroundImage ? 
            `url(${badgeConfig.backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Render badge fields */}
        {badgeConfig.fields?.map((field) => {
          const placed = previewLayout.get(field);
          if (!placed) return null;

          const fieldValue = placed.value;
          const x = inchesToPixels(field.x) * scaleFactorX;
          const y = inchesToPixels(placed.y) * scaleFactorY;
          const width = inchesToPixels(field.width) * scaleFactorX;
          const height = inchesToPixels(field.height) * scaleFactorY;
          
          // Get rotation style
          const rotation = field.style?.rotation || 0;

          return (
            <div
              key={field.id}
              className="absolute"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                width: `${width}px`,
                height: `${height}px`,
                transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
                transformOrigin: 'center center'
              }}
            >
              {field.type === 'text' && (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    ...(textOverflowMode(field) === 'shrink'
                      ? { whiteSpace: 'nowrap', overflow: 'hidden' }
                      : { whiteSpace: 'normal', overflowWrap: 'anywhere', overflow: 'visible' }),
                    fontSize: `${getDynamicFontSize(fieldValue, field) * PX_PER_PT * Math.min(scaleFactorX, scaleFactorY)}px`,
                    color: field.style?.color || '#000000',
                    fontWeight: field.style?.fontWeight || 'normal',
                    fontFamily: field.style?.fontFamily || 'Arial, sans-serif',
                    textAlign: 'center', // Always center-align text
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                  }}
                >
                  <span style={{ maxWidth: '100%', minWidth: 0 }}>{fieldValue}</span>
                </div>
              )}
              
              {field.type === 'image' && (
                <img
                  src={fieldValue}
                  alt={field.label}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/100x100.png?text=Photo';
                  }}
                />
              )}
              
              {field.type === 'qrCode' && fieldValue && (
                <img
                  src={fieldValue}
                  alt="QR Code"
                  className="w-full h-full object-contain"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

DynamicBadgeRenderer.displayName = 'DynamicBadgeRenderer';

export default DynamicBadgeRenderer;
